import { ValidatorFn, Validators } from '@angular/forms';
import {
  EntityReference,
  ProductQuantityUnit,
  ProductStockItem,
  TransferProductAction,
} from '../../core/api/api.entities';
import { ProductCategoryStockItemRow } from '../../features/stock/product-categories/models/product-category-stock-item-row.model';
import { positive } from '../form-control-options.constants';
import { QuickActionTransferFormControls } from '../form-controls/quick-action.form-controls';
import { QuickActionForm } from './quick-action.form';

export class QuickActionTransferForm extends QuickActionForm<QuickActionTransferFormControls> {
  formGroup = this.createFormGroup(QuickActionTransferFormControls);

  get amountHint(): string | undefined {
    return undefined;
  }

  override get locationOptions(): EntityReference[] {
    return this.options.locations.filter(l => l.id !== this.controls.fromLocation.value?.id);
  }

  get productAction(): TransferProductAction {
    if (!this.formGroup.valid) {
      throw new Error('Form is invalid');
    }

    const controls = this.controls;

    return {
      amount: this.amount,
      fromLocationId: controls.stockItem.value!.locationId!,
      stockItemId: controls.stockItem.value!.id,
      toLocationId: controls.toLocation.value!.id,
    };
  }

  get quantityUnit(): ProductQuantityUnit | undefined {
    return this.product?.quantityUnitStock;
  }

  override set stockItem(stockItem: ProductCategoryStockItemRow | undefined) {
    super.stockItem = stockItem;
    this.controls.fromLocation.setValue(this.options.locations.find(l => l.id === stockItem?.locationId)!);
    this.controls.fromLocation.disable();
    this.controls.toLocation.setValue(this.options.locations[0]);
  }

  setAmountToMax(): void {
    if (this.product && this.controls.stockItem.value) {
      this.controls.amount.setValue(this.controls.stockItem.value.amount);
    }
  }

  override canSelectStockItem(stockItem: ProductStockItem): boolean {
    return stockItem.amount > 0;
  }

  protected setChangeListeners(): void {
    this.controls.fromLocation.valueChanges.subscribe(() => {
      this.controls.amount.setValidators([
        Validators.required,
        Validators.min(0.000001),
        Validators.max(this.maxAmount || 0),
      ]);

      this.controls.amount.updateValueAndValidity();
    });

    this.controls.stockItem.valueChanges.subscribe(stockEntry => {
      if (stockEntry) {
        this.controls.amount.setValidators([
          Validators.required,
          Validators.min(0.000001),
          Validators.max(this.maxAmount || 0),
        ]);
        this.controls.amount.setValue(stockEntry.amount);
        this.controls.amount.updateValueAndValidity();

        this.controls.toLocation.setValue(this.options.locations.find(l => l.id === stockEntry.locationId)!);
      }
    });
  }

  protected setInitialValues(): void {
    this.controls.amount.setValidators([...(positive.validators as ValidatorFn[]), Validators.max(0)]);

    if (this.product) {
      if (this.product?.activeStockItems.length === 0) {
        this.disableForm();
      } else {
        const stockItem = this.product?.activeStockItems[0];
        this.controls.stockItem.setValue(stockItem);
        this.controls.fromLocation.setValue(this.options.locations.find(l => l.id === stockItem.locationId)!);
        this.controls.toLocation.setValue(this.options.locations.find(l => l.id === stockItem.locationId)!);
        this.enableForm();
        this.controls.amount.setValidators([...(positive.validators as ValidatorFn[]), Validators.max(this.maxAmount)]);
      }
    }
  }

  private disableForm(): void {
    this.controls.amount.disable();
    this.controls.stockItem.disable();
    this.controls.toLocation.disable();
    this.formGroup.markAllAsTouched();
  }

  private enableForm(): void {
    this.controls.amount.enable();
    this.controls.stockItem.enable();
    this.controls.toLocation.enable();
  }

  private get maxAmount(): number {
    if (this.controls.stockItem.value) {
      return this.controls.stockItem.value.amount;
    }

    return this.product?.activeStockItems.find(s => s.locationId === this.controls.fromLocation.value?.id)?.amount || 0;
  }
}
